import { jumboMediaAPI } from "./service";

// User-facing re-delivery builder endpoints (Roadmap item 8): eligibility lookup, flat price, and
// the pending-order create. Payment is confirmed server-side by the iCount redirect to
// api/ReDelivery/ConfirmPayment (hard gate) — not an endpoint the SPA calls directly.

export type ReDeliveryVersionBroadcasterT = {
  BroadcasterId: number;
  Name: string;
  HouseNumber: string | null;
  ExcludedReason?: string;
};

export type ReDeliveryVersionT = {
  ProjectFileId: number;
  AdId: string;
  Name: string;
  FileType: number;
  RequiredLength: number;
  SourceProjectId: number;
  SourceDeliveryNumber: number;
  ValidBroadcasters: ReDeliveryVersionBroadcasterT[];
  ExcludedBroadcasters: ReDeliveryVersionBroadcasterT[];
};

export type ReDeliveryBroadcasterT = { BroadcasterId: number; Name: string };

export type ReDeliverySourceInfoT = {
  ProjectId: number;
  Number: number;
  Name: string;
  CampaignId: number;
  CampaignNumber: string | null;
  BrandId: number;
  BrandName: string | null;
  AdvertiserId: number;
  AdvertiserName: string | null;
  AdvertisingAgencyId: number;
  PostProductionCompanyId: number;
  MediaCompanyId: number | null;
  ProductionCompanyId: number | null;
  IndustryId: number | null;
  IndustryNameEn: string | null;
  IndustryNameHe: string | null;
  BroadcastingDate: string | null;
};

export type ReDeliveryLookupResultT = {
  IsEligible: boolean;
  Reason: number;
  ReasonCode: string;
  MinAgeDays: number;
  Source: ReDeliverySourceInfoT | null;
  Versions: ReDeliveryVersionT[];
  UnionBroadcasters: ReDeliveryBroadcasterT[];
};

export type ReDeliveryPriceT = {
  BroadcasterCount: number;
  PricePerBroadcaster: number;
  PriceExVat: number;
  VatPercentage: number;
  Vat: number;
  Total: number;
  Currency: number;
};

export type ReDeliveryOrderT = {
  Id: number;
  OrderStatus: number;
  BroadcasterCount: number;
  PricePerBroadcaster: number;
  Price: number;
  Vat: number;
  Currency: number;
  ChargeSanityString: string;
  // Anti-forgery token minted at order creation (spec-13 §3 step 1). Served ONLY to the owning
  // user (GetOrder is tenant-scoped; admin view models never include it). Echoed to the paypage
  // as m__token, which iCount returns to the success URL as `token` — the confirm gate's authorization.
  ConfirmToken: string | null;
  confirmation_code: string | null;
  ProjectId: number | null;
  CampaignId: number | null;
  SourceDeliveryNumbers: string | null;
  // Created delivery/campaign numbers + iCount invoice URL — for the confirmation page.
  DeliveryNumber: number | null;
  CampaignNumber: string | null;
  InvoiceUrl: string | null;
  // Customer details (filled on the customer/billing page).
  CompanyName: string | null;
  ContactName: string | null;
  EmailAddress: string | null;
  PhoneNumber: string | null;
  InvoiceName: string | null;
  VATNumber: string | null;
  // Serialized selection snapshot — parse to ReDeliverySelectionT for the dedicated pages.
  SelectionJson: string | null;
};

// Parsed from ReDeliveryOrderT.SelectionJson — drives the version + customer/billing pages.
export type ReDeliverySelectionT = {
  EntryType: string;
  ExistingCampaignId: number | null;
  NewCampaignName: string | null;
  BroadcastingDate: string | null;
  SourceProjectNumbers: number[];
  SelectedProjectFileIds: number[];
  TargetBroadcasterIds: number[];
  OfferedVersions: ReDeliveryVersionT[];
  Broadcasters: ReDeliveryBroadcasterT[];
  Source: ReDeliverySourceInfoT | null;
};

export const reDeliveryAPI = jumboMediaAPI.injectEndpoints({
  endpoints: (build) => ({
    getReusableDelivery: build.query<
      ReDeliveryLookupResultT,
      { number: number; brandName?: string; brandId?: number; appendToNumber?: number }
    >({
      query: ({ number, brandName, brandId, appendToNumber }) => {
        const params = new URLSearchParams();
        params.set("number", String(number));
        // brandId is the exact match (the dropdown option value is the Brand.Id); brandName is kept
        // as a tolerant fallback for the backend. Send brandId whenever we have it.
        if (brandId) params.set("brandId", String(brandId));
        if (brandName) params.set("brandName", brandName);
        if (appendToNumber) params.set("appendToNumber", String(appendToNumber));
        return { url: `/api/ReDelivery/GetReusableDelivery?${params.toString()}` };
      },
    }),
    getReDeliveryPrice: build.query<ReDeliveryPriceT, { broadcasterCount: number }>({
      query: ({ broadcasterCount }) => ({
        url: `/api/ReDelivery/GetReDeliveryPrice?broadcasterCount=${broadcasterCount}`,
      }),
    }),
    // spec-38 §R2/R3 — the confirmation page is reached by an OPAQUE token instead of the raw
    // sequential order id. `orderId` is whatever the route gave us, so send it as `token` when it is
    // not a plain number and as `orderId` when it is — the same switch projectsApi.getOrderData uses
    // on the delivery-service leg. Both forms are accepted by the backend and authorized identically
    // (D3), so old links, bookmarks and the admin grid keep working unchanged.
    //
    // 🔴 The call sites must stop coercing with `+orderId`: that is NaN for a token, and NaN silently
    // produces `?orderId=NaN`. The type below is `number | string` for exactly that reason.
    getReDeliveryOrder: build.query<ReDeliveryOrderT, { orderId: number | string }>({
      query: ({ orderId }) => ({
        url: /^\d+$/.test(String(orderId))
          ? `/api/ReDelivery/GetOrder?orderId=${orderId}`
          : `/api/ReDelivery/GetOrder?token=${encodeURIComponent(String(orderId))}`,
      }),
      providesTags: ["ReDeliveryOrders"],
    }),
    // spec-38 §R2 — mints the opaque identifier for an order's confirmation page. Guarded server-side
    // by the SAME ownership rule as reading the order, so it 404s for anyone but the owning client (or
    // a GlobalAdmin).
    //
    // 🔴 NOT the order's ConfirmToken. That one is the iCount payment anti-forgery secret already on
    // ReDeliveryOrderT — persisted, random, and echoed to the paypage. This one is a derived,
    // never-persisted encryption of the order id whose only job is to keep the id out of the URL.
    // Swapping them is a security bug.
    getReDeliveryConfirmationToken: build.query<
      { Token: string },
      { orderId: number | string }
    >({
      query: ({ orderId }) => ({
        url: `/api/ReDelivery/GetReDeliveryConfirmationToken?orderId=${orderId}`,
      }),
    }),
    createReDeliveryOrder: build.mutation<ReDeliveryOrderT, any>({
      query: (body) => ({ url: `/api/ReDelivery/CreateOrder`, method: "POST", body }),
      invalidatesTags: ["ReDeliveryOrders"],
    }),
    // Version-selection page: replace the chosen versions on a pending order.
    updateReDeliveryOrderSelection: build.mutation<
      ReDeliveryOrderT,
      { OrderId: number; SelectedProjectFileIds: number[] }
    >({
      query: (body) => ({
        url: `/api/ReDelivery/UpdateOrderSelection`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ReDeliveryOrders"],
    }),
    // Customer/billing page: save the client-information form before opening payment.
    updateReDeliveryOrderCustomer: build.mutation<
      ReDeliveryOrderT,
      { orderId: number; body: any }
    >({
      query: ({ orderId, body }) => ({
        url: `/api/ReDelivery/UpdateOrderCustomer?orderId=${orderId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ReDeliveryOrders"],
    }),
  }),
});

export const {
  useLazyGetReusableDeliveryQuery,
  useGetReDeliveryPriceQuery,
  useGetReDeliveryOrderQuery,
  useLazyGetReDeliveryConfirmationTokenQuery,
  useCreateReDeliveryOrderMutation,
  useUpdateReDeliveryOrderSelectionMutation,
  useUpdateReDeliveryOrderCustomerMutation,
} = reDeliveryAPI;
